import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FilterChips } from '@user/components/HereMap/FilterChips'
import Box from '@mui/material/Box'
import { LocateMeButton } from '@user/components/HereMap/LocateMeButton'
import { ZoomControls } from '@user/components/HereMap/ZoomControls'
import { LayerControls } from '@user/components/HereMap/LayerControls'
import { HereMapDefaultLayers } from '@user/components/HereMap/types'
import { useRightDragZoom } from '@user/components/HereMap/hooks/useRightDragZoom'
import { useTerritoryLayer } from '@user/store/territory/hooks/useTerritoryLayer'
import { ViewportBounds } from '@user/store/territory'
import { useD2DTerritoriesEnabled } from '@user/store/tenantFeatures/hooks/useD2DTerritoriesEnabled'
import { useTerritoryDraw } from '@user/components/HereMap/HereMapNew/hooks/useTerritoryDraw'
import { useTerritoryLabels } from '@user/components/HereMap/HereMapNew/hooks/useTerritoryLabels'
import {
  useTerritoryInteraction,
  TerritorySelection,
} from '@user/components/HereMap/HereMapNew/hooks/useTerritoryInteraction'
import { TerritoryDrawBanner } from '@user/components/HereMap/TerritoryDraw/TerritoryDrawBanner'
import { TerritoryCreateDialog } from '@user/components/HereMap/TerritoryDraw/TerritoryCreateDialog'
import { TerritoryManageButton } from '@user/components/HereMap/TerritoryDraw/TerritoryManageButton'
import { TerritoryManagePanel } from '@user/components/HereMap/TerritoryDraw/TerritoryManagePanel'
import { TerritoryDeleteDialog } from '@user/components/HereMap/TerritoryDraw/TerritoryDeleteDialog'
import { TerritoryPerformanceDrawer } from '@user/components/HereMap/TerritoryDraw/TerritoryPerformanceDrawer'
import { TerritoryMergeDialog } from '@user/components/HereMap/TerritoryDraw/TerritoryMergeDialog'
import { TerritorySummaryBubble } from '@user/components/HereMap/TerritoryDraw/TerritorySummaryBubble'
import { TerritoryNameDialog } from '@user/components/HereMap/TerritoryDraw/TerritoryNameDialog'
import { TerritoryOverlapConfirmDialog } from '@user/components/HereMap/TerritoryDraw/TerritoryOverlapConfirmDialog'
import { findTerritoryOverlaps } from '@user/store/territory/overlapDetection'
import {
  cancelTerritoryDraw,
  finishTerritoryDraw,
  finishTerritoryRedraw,
  selectParkedRule,
  selectRedrawTerritoryId,
  selectTerritoryDrawOnMap,
} from '@user/store/territoryDraw'
import { updateTerritory } from '@user/store/territory/api'
import { setMapViewDialogOpen } from '@user/store/map/slice'
import {
  fetchTerritoryBoundaries,
  selectTerritoryBoundariesById,
  selectTerritorySummaries,
} from '@user/store/territory'
import { toast } from '@sales-center/components'
import { selectCurrentUser } from '@user/store/authorization'
import type {
  Territory as TerritoryType,
  TerritoryBoundary,
} from '@user/store/territory/typings'
import type { LassoRejection } from '@user/store/territory/lassoGeometry'
import { useData, useMapEvents } from '@user/components/HereMap/hooks'
import { useMapLibreEvents } from '@user/components/HereMap/hooks/useMapLibreEvents'
import { useHereMapInstance } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import { useMapLibreInstance } from '@user/components/HereMap/HereMapNew/hooks/useMapLibreInstance'
import { useHereMapMarkers } from '@user/components/HereMap/HereMapNew/hooks/useHereMapMarkers'
import { useMapLibreMarkers } from '@user/components/HereMap/HereMapNew/hooks/useMapLibreMarkers'
import { MapPinTooltip } from '@user/components/HereMap/MapPin/components/MapPinTooltip/MapPinTooltip'
import { MapLibreMapPinTooltip } from '@user/components/HereMap/MapPin/components/MapPinTooltip/MapLibreMapPinTooltip'
import { trySwapPestPacDrawer } from '@user/components/HereMap/utils/pestPacDrawerSwap'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import {
  selectContactLocations,
  selectMapLinkClickedContactLocation,
  selectSalespersonFilterOptions,
  selectSalespersonFilterSelection,
} from '@user/store/map'
import { EntityLocationDTO } from '@user/store/map/typings'
import MapLoadingProgress from '@user/features/MapView/MapLoadingProgress/MapLoadingProgress'
import { useHandler } from '@user/components/HereMap/MapPin/components/MapPinTooltip/hooks/useHandler'
import MultiTenantDrawer from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawer'
import EntityDetailsDrawer from '@user/features/MapView/EntityDetailsDrawer/EntityDetailsDrawer'
import useMediaQuery from '@mui/material/useMediaQuery'
import FilterDrawer from '@user/features/MapView/FilterDrawer/FilterDrawer'
import { MapViewDialogContentProps } from '@user/features/MapView/MapViewDialog/MapViewDialogContent'
import SearchDrawer from '@user/features/MapView/MapViewDialog/SearchDrawer/SearchDrawer'
import { AddLeadModal } from '@user/features/MapView/AddLeadModal'
import {
  KnockStatusHistoryModal,
  KnockStatusModal,
} from '@user/features/MapView/KnockStatusModal'
import { RefreshButton } from '../RefreshButton'
import { RemoveMapBoundaryButton } from '../RemoveMapBoundaryButton'
import { AddLeadMapPinTooltip } from '../MapPin/components/AddLeadMapPinTooltip/AddLeadMapPinTooltip'
import { MapLibreAddLeadMapPinTooltip } from '../MapPin/components/AddLeadMapPinTooltip/MapLibreAddLeadMapPinTooltip'
import { KeyInformation } from '../KeyInformation/KeyInformation'
import { CollisionToggle } from '../CollisionToggle'
import { HereResult } from '@user/store/services/hereGeoCoder'
import { useRefreshOnTabFocus } from '@user/features/MapView/hooks/useRefreshOnTabFocus'
import { useSalesPersonFilterEnabled } from '@user/store/tenantFeatures/hooks/useSalesPersonFilterEnabled'
import { SalespersonFilterButton } from '../SalespersonFilterButton/SalespersonFilterButton'
import { SalespersonFilterModal } from '../SalespersonFilterModal/SalespersonFilterModal'
import { useSalespersonFilterPicklist } from './hooks/useSalespersonFilterPicklist'
import { isPinVisibleForSalespersonFilter } from './hooks/useSalespersonFilter.utils'
import { isSameLocationIdentity } from '@user/utils/locationMatching'
import { DEFAULT_TERRITORY_COLOR } from '@user/store/territory/constants'

interface HereMapProps extends MapViewDialogContentProps {
  center?: H.geo.Point
  zoom?: number
  isMapRendering?: boolean
  refreshMapPins?: (
    forceUpdate: boolean,
    topLeft?: H.geo.Point,
    bottomRight?: H.geo.Point,
    center?: H.geo.Point,
    zoom?: number,
  ) => void
}

const USE_MAPLIBRE = true

export const HereMapNew: React.FC<HereMapProps> = ({
  center: centerProp,
  zoom: zoomProp,
  refreshMapPins,
  isMapRendering,
  setFilterDrawerOpen,
  filterDrawerOpen,
  setSearchDrawerOpen,
  searchDrawerOpen,
  open,
  onClose: onCloseProp,
}) => {
  const animatePositionChange = true
  const salesPersonFilterEnabled = useSalesPersonFilterEnabled()
  const salespersonFilterSelection = useSelector(
    selectSalespersonFilterSelection,
  )
  const salespersonFilterOptions = useSelector(selectSalespersonFilterOptions)
  const mapRef = useRef<HTMLDivElement>(null)
  // Bottom edge of the filter-chips stack, reported by FilterChips, so the
  // loading pill can sit just below the chips instead of overlapping them.
  const [chipsBottom, setChipsBottom] = useState(0)
  const [reviewDraftId, setReviewDraftId] = useState<string | null>(null)
  const [salespersonFilterModalOpen, setSalespersonFilterModalOpen] =
    useState(false)
  const contactLocations = useSelector(selectContactLocations)
  const isUnfitBothModals = useMediaQuery(`(max-width: 1007px)`)
  const mapLinkClickedContactLocation = useSelector(
    selectMapLinkClickedContactLocation,
  )

  const {
    locationResolved,
    center,
    zoom,
    hoveredPin,
    setHoveredPin,
    clickedPin,
    setClickedPin,
    tooltipHovered,
    setTooltipHovered,
    drawerOpen,
    setDrawerOpen,
    multiTenantDrawerOpen,
    setMultiTenantDrawerOpen,
    foundLocationFromSearch,
    setFoundLocationFromSearch,
    addressDetailsForAddLead,
    setAddressDetailsForAddLead,
    addressDetailsForAddLeadTemp,
    setAddressDetailsForAddLeadTemp,
    addLeadModalOpen,
    setAddLeadModalOpen,
    refreshTrigger,
    triggerRefresh,
    openKnockStatusModal,
    setOpenKnockStatusModal,
    convertHereCoordsToEntityLocation,
    showHistory,
    setShowHistory,
    addressDetailsForKnockStatus,
    setAddressDetailsForKnockStatus,
    setAddressDetailsForKnockStatusTemp,
    pendingCustomReason,
    setPendingCustomReason,
    convertEntityLocationToHereEvent,
  } = useData({
    center: centerProp,
    zoom: zoomProp,
  })

  const handleReviewPendingLead = useCallback(
    (draftId: string) => {
      setReviewDraftId(draftId)
      setAddLeadModalOpen(true)
    },
    [setAddLeadModalOpen],
  )

  const {
    onMarkerHover,
    onMarkerClick,
    cancelHoverOpen,
    markPinClicked,
    isHoverPreview,
    handleMapClick,
    onClose,
    onActionClick,
    closeAllDrawers,
    closeAndRefresh,
    handleLocationPreviewAddLead,
    handleAddressDetailsForAddLeadClose,
    handleAddLeadModalClose,
    handleAddLeadSaveSuccess,
    handleDuplicateManageClick,
    handleKnockStatusModalClose,
    handleLocationPreviewKnockStatus,
    handleLocationPreviewKnockStatusHistory,
    handleUpdateAddressDetails,
    viewClickHandler,
    viewMultiTenantClickHandler,
    onKnockStatusDeleteSuccess,
    onSaveNewKnockStatusLocation,
  } = useHandler({
    setHoveredPin,
    clickedPin,
    setClickedPin,
    tooltipHovered,
    drawerOpen,
    setDrawerOpen,
    multiTenantDrawerOpen,
    setMultiTenantDrawerOpen,
    setSearchDrawerOpen,
    setFoundLocationFromSearch,
    setFilterDrawerOpen,
    filterDrawerOpen,
    setTooltipHovered,
    setAddressDetailsForAddLead,
    addressDetailsForAddLead,
    setAddressDetailsForAddLeadTemp,
    addressDetailsForAddLeadTemp,
    searchDrawerOpen,
    open,
    setAddLeadModalOpen,
    onCloseProp,
    setOpenKnockStatusModal,
    setShowHistory,
    setAddressDetailsForKnockStatus,
    setAddressDetailsForKnockStatusTemp,
    convertHereCoordsToEntityLocation,
    convertEntityLocationToHereEvent,
    setPendingCustomReason,
    onReviewPendingLead: handleReviewPendingLead,
  })

  // Both hooks are always called (React rules). The inactive one is no-oped by
  // passing locationResolved:false so it never initialises a map instance.
  const {
    map: hereMap,
    defaultLayers,
    mounted: hereMounted,
    contactPinsGroup,
    knockStatusGroup,
    currentLocationGroup,
  } = useHereMapInstance({
    containerRef: mapRef,
    center,
    zoom,
    onMapClick: handleMapClick,
    locationResolved: USE_MAPLIBRE ? false : locationResolved,
  })

  const { map: mlMap, mounted: mlMounted } = useMapLibreInstance({
    containerRef: mapRef,
    center,
    zoom,
    onMapClick: handleMapClick,
    locationResolved: USE_MAPLIBRE ? locationResolved : false,
  })

  const map = USE_MAPLIBRE ? null : hereMap
  const mounted = USE_MAPLIBRE ? mlMounted : hereMounted

  const loading = React.useMemo(() => isMapRendering, [isMapRendering])

  // First click on a pin shows the preview bubble. Only open the drawer
  // directly when one is already open (switching entities without closing).
  const handleMapLibreMarkerClick = useCallback(
    (location: EntityLocationDTO) => {
      // A click opens immediately — abort any pending 2s hover-open (WWM-17927).
      cancelHoverOpen()
      markPinClicked()
      if (location.entityId === 0) {
        // PP pins clicked while the drawer is open swap it to the new location
        // (SH-1424). Shared with onMarkerClick (useHandler) via trySwapPestPacDrawer.
        if (
          trySwapPestPacDrawer(location, drawerOpen || multiTenantDrawerOpen, {
            setHoveredPin,
            setClickedPin,
            setAddressDetailsForAddLeadTemp,
            viewClickHandler,
          })
        ) {
          return
        }
        // KS-only pin: show the AddLead/KS preview tooltip (mirrors onMarkerClick
        // in useHandler). onMarkerHover is blocked when a bubble is already open
        // (clickedPinRef guard), so the click must handle this case explicitly.
        setHoveredPin(null)
        setClickedPin(location)
        setAddressDetailsForAddLeadTemp(
          convertEntityLocationToHereEvent(location),
        )
        return
      }
      setHoveredPin(null)
      setClickedPin(location)
      setAddressDetailsForAddLeadTemp(null)
      if (drawerOpen || multiTenantDrawerOpen) {
        if (location.multitenant) {
          viewMultiTenantClickHandler(location.entities)
        } else {
          viewClickHandler(location, true)
        }
      }
    },
    [
      cancelHoverOpen,
      markPinClicked,
      setClickedPin,
      setHoveredPin,
      setAddressDetailsForAddLeadTemp,
      convertEntityLocationToHereEvent,
      viewClickHandler,
      viewMultiTenantClickHandler,
      drawerOpen,
      multiTenantDrawerOpen,
    ],
  )

  useHereMapMarkers({
    map,
    onMarkerClick,
    onMarkerHover,
    activePin: clickedPin || hoveredPin,
    contactPinsGroup,
    knockStatusGroup,
    currentLocationGroup,
  })

  const { lastKnownPositionRef } = useMapLibreMarkers({
    map: USE_MAPLIBRE ? mlMap : null,
    onMarkerClick: handleMapLibreMarkerClick,
    onMarkerHover,
    activePin: clickedPin || hoveredPin,
  })

  useSalespersonFilterPicklist()

  // Territory layer (WWM-17508). The polygons need the same viewport the pins get, so rather
  // than adding a second moveend listener this records the bounds on the way through the existing
  // onViewChange path. Both the MapLibre and the dormant HERE path go through it, so territories
  // behave the same on either renderer.
  const territoriesEnabled = useD2DTerritoriesEnabled()
  const [territoryViewport, setTerritoryViewport] =
    useState<ViewportBounds | null>(null)

  const handleViewChange = useCallback(
    (
      forceUpdate: boolean,
      topLeft?: { lat: number; lng: number },
      bottomRight?: { lat: number; lng: number },
      center?: { lat: number; lng: number },
      zoom?: number,
    ) => {
      // Skip entirely when the feature is off: useTerritoryLayer no-ops, so allocating a new
      // viewport object on every debounced moveend would re-render this tree for nothing.
      if (territoriesEnabled && topLeft && bottomRight) {
        // topLeft is north-west and bottomRight south-east, so normalize rather than assume:
        // overlapsViewport compares mins against mins.
        setTerritoryViewport({
          minLatitude: Math.min(topLeft.lat, bottomRight.lat),
          maxLatitude: Math.max(topLeft.lat, bottomRight.lat),
          minLongitude: Math.min(topLeft.lng, bottomRight.lng),
          maxLongitude: Math.max(topLeft.lng, bottomRight.lng),
        })
      }

      refreshMapPins?.(
        forceUpdate,
        topLeft as never,
        bottomRight as never,
        center as never,
        zoom,
      )
    },
    [refreshMapPins, territoriesEnabled],
  )

  // Fetches summaries once, then geometry for whatever this viewport touches. No-ops entirely
  // when the tenant feature flag is off.
  useTerritoryLayer(territoryViewport)

  // Territory drawing (WWM-17508, Feature A). Admin-only, matching the
  // [Authorize(Roles = "Admin")] on TerritoryController's write endpoints -- showing the control
  // to a rep who would get a 403 on save is worse than not showing it.
  const { isAdmin } = useSelector(selectCurrentUser)
  const canDrawTerritories = territoriesEnabled && isAdmin && USE_MAPLIBRE

  const [drawActive, setDrawActive] = useState(false)
  const [draftBoundary, setDraftBoundary] = useState<TerritoryBoundary | null>(
    null,
  )
  // Headline AND body together, rather than a bare string with the banner guessing a headline.
  // The banner used to fall back to "That shape crossed over itself" for any hint with no title,
  // and only the split-by-line case ever passed one -- so an admin whose redraw failed to SAVE,
  // or whose stroke was interrupted, was told their shape crossed itself. Carrying the pair
  // makes every hint state what it is about at the point it is raised.
  const [drawHint, setDrawHint] = useState<{
    title: string
    body: string
  } | null>(null)
  const [manageOpen, setManageOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<TerritoryType | null>(null)
  const [performanceOpen, setPerformanceOpen] = useState(false)

  // The territory whose summary bubble is open, and where it was clicked. Null is the normal
  // state -- the bubble is opened by a click and dismissed by the next one off any territory.
  const [selectedTerritory, setSelectedTerritory] =
    useState<TerritorySelection | null>(null)

  // Drawing a territory FOR AN ASSIGNMENT RULE (WWM-17508). The admin was filling in a rule,
  // asked for a new territory, and the rules page sent them here with the form parked. There is
  // no draw button on the map any more: a territory exists to serve a rule, so this is the way
  // one gets drawn.
  const drawingForRule = useSelector(selectTerritoryDrawOnMap)
  const parkedRule = useSelector(selectParkedRule)
  // Set when the trip is to RESHAPE a territory the rule already uses, rather than add one.
  const redrawTerritoryId = useSelector(selectRedrawTerritoryId)
  const parkedRuleName =
    typeof parkedRule?.name === 'string' ? parkedRule.name : undefined

  // The finished shape, waiting to be named. Deliberately NOT draftBoundary: that opens the full
  // create dialog, which asks for the team and owners the rule is about to ask for.
  const [namingForRule, setNamingForRule] = useState<TerritoryBoundary | null>(
    null,
  )
  const [savingRedraw, setSavingRedraw] = useState(false)
  /**
   * A redraw-for-rule whose new outline lands on another territory, held back for confirmation.
   * Null on the normal path, which still saves without asking.
   */
  const [redrawConfirm, setRedrawConfirm] = useState<{
    territory: TerritoryType
    boundary: TerritoryBoundary
  } | null>(null)

  // The territory a drawn piece will be carved OUT OF. Distinct from redrawingFor, which
  // REPLACES a boundary -- the same lasso, two different destinations for the shape.
  const [splittingFrom, setSplittingFrom] = useState<TerritoryType | null>(null)

  // How the split is being drawn. A line across is the default because it is the gesture people
  // reach for when dividing something, and it is a fraction of the work of tracing a whole half
  // -- but a loop is still the only way to carve an irregular piece out of the middle, so both
  // stay available and the banner offers the other one.
  const [splitMode, setSplitMode] = useState<'line' | 'area'>('line')

  // The cut drawn across a territory. The counterpart of draftBoundary for the line gesture:
  // either one of them opens the naming dialog, and never both at once.
  const [splitLine, setSplitLine] = useState<number[][] | null>(null)
  const [merging, setMerging] = useState<TerritoryType[] | null>(null)

  // The territory being edited. `editing` holds its details; when the user chooses to redraw,
  // redrawingFor holds it instead and the next completed lasso replaces that boundary.
  const [editing, setEditing] = useState<TerritoryType | null>(null)
  const [redrawingFor, setRedrawingFor] = useState<TerritoryType | null>(null)

  const dispatch = useAppDispatch()
  const territorySummaries = useSelector(selectTerritorySummaries)
  const territoryBoundaries = useSelector(selectTerritoryBoundariesById)

  // Arriving from a rule. The banner takes over and offers the pen -- it is NOT armed here.
  //
  // Draw mode disables panning, so arming on arrival dropped the admin onto whatever the map was
  // last showing with no way to reach the area they came to draw. Finding the place is the first
  // half of the job, and it needs the map to still behave like a map.
  useEffect(() => {
    if (!canDrawTerritories) return

    if (drawingForRule) {
      setManageOpen(false)
      setSelectedTerritory(null)
      return
    }

    // The trip is over (saved or discarded): make sure the pen is not left armed over a map the
    // user is now browsing normally.
    setNamingForRule(null)
    setDrawActive(false)
  }, [canDrawTerritories, drawingForRule])

  // Escape leaves draw mode. The mode disables panning, so a user who cannot find the button --
  // or who turned it on by accident -- would otherwise be stuck with a map that will not pan.
  // Not bound while the dialog is up: MUI already closes that on Escape, and stealing the key
  // would discard the shape instead.
  useEffect(() => {
    if (!canDrawTerritories || !drawActive || draftBoundary || splitLine) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setDrawActive(false)
      setDrawHint(null)
      // Disarm the split too. Escape lowered drawActive and left splittingFrom and splitMode
      // set, so the next "Redraw boundary" on a DIFFERENT territory started the pen in line
      // mode -- the mode prop reads splittingFrom && splitMode === 'line' -- and finishing that
      // stroke called onCompleteLine, opening the split dialog for the first territory instead
      // of redrawing the second. Only the banner's Cancel and a completed save cleared these.
      setSplittingFrom(null)
      setSplitMode('line')
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canDrawTerritories, drawActive, draftBoundary, splitLine])

  // Moves the map to a territory. Every action that acts on one calls this first: editing or
  // deleting a shape you cannot see is how the wrong "Monmouth North"/"Monmouth South" gets
  // changed. fitBounds is safe for a degenerate envelope -- a territory always has area.
  const zoomToTerritory = useCallback(
    (territory: TerritoryType) => {
      const b = territory.bounds
      mlMap?.fitBounds(
        [
          [b.minLongitude, b.minLatitude],
          [b.maxLongitude, b.maxLatitude],
        ],
        { padding: 64, duration: 600 },
      )
    },
    [mlMap],
  )

  // A redraw knows exactly where it is going, so it goes there. Nothing to hunt for, and the
  // outline being replaced is on screen while it is replaced.
  useEffect(() => {
    if (!canDrawTerritories || !drawingForRule || redrawTerritoryId === null) {
      return
    }

    const target = territorySummaries.find((t) => t.id === redrawTerritoryId)
    if (target) zoomToTerritory(target)
  }, [
    canDrawTerritories,
    drawingForRule,
    redrawTerritoryId,
    territorySummaries,
    zoomToTerritory,
  ])

  // Names on the polygons. Shown to everyone the layer is shown to -- a rep benefits from
  // knowing whose patch they are looking at as much as an admin does -- but hidden while
  // drawing, when a label under the pen is in the way.
  // The name label is the way into a territory's summary once the map is zoomed in far enough
  // for a click to mean "add a lead at this address" instead. See useTerritoryInteraction.
  useTerritoryLabels({
    map: territoriesEnabled && USE_MAPLIBRE ? mlMap : null,
    territories: territorySummaries,
    enabled: !drawActive,
    onSelect: setSelectedTerritory,
  })

  // Hover on the polygons, and click on them while zoomed out past the add-lead threshold.
  // Shown to everyone the layer is shown to, like the labels: the numbers come from records the
  // caller can already see, and a rep asking "whose patch is this and what is in it" is the
  // question the feature exists to answer.
  //
  // Off while drawing, and while a finished stroke waits in its dialog: the lasso owns the
  // canvas then, and the click that ends a stroke would otherwise land on whatever is beneath.
  useTerritoryInteraction({
    map: territoriesEnabled && USE_MAPLIBRE ? mlMap : null,
    enabled: !drawActive && !draftBoundary && !splitLine,
    territories: territorySummaries,
    selectedId: selectedTerritory?.territoryId ?? null,
    onSelect: setSelectedTerritory,
  })

  // Entering draw mode closes the bubble. It is anchored to a point on the map the user is
  // about to draw over, and nothing else would clear it -- the interaction hook stops
  // listening the moment drawing starts.
  useEffect(() => {
    if (drawActive) setSelectedTerritory(null)
  }, [drawActive])

  // A territory the bubble is open on can be deleted, merged away or redrawn from the manage
  // panel. Dropping the selection when it leaves the summaries keeps the bubble from
  // describing something that is no longer on the map.
  const selectedTerritoryRecord = selectedTerritory
    ? territorySummaries.find((t) => t.id === selectedTerritory.territoryId)
    : undefined

  /**
   * Replace a territory's outline and go back to the rule.
   *
   * Shared by the straight-through redraw and the one that had to be confirmed, so "save anyway"
   * cannot drift away from the save it is agreeing to.
   */
  const saveRedraw = useCallback(
    (existing: TerritoryType, boundary: TerritoryBoundary) => {
      setSavingRedraw(true)
      // PUT is a full replace, so every field is resent -- including the territory's own colour,
      // or a redraw would silently recolour it. The dispatch cast keeps the promise this awaits.
      dispatch(
        updateTerritory({
          id: existing.id,
          name: existing.name,
          salesTeamId: existing.salesTeamId,
          ownerIds: existing.ownerIds,
          colorHex: existing.colorHex ?? DEFAULT_TERRITORY_COLOR,
          boundary,
        }),
      ).then((result: any) => {
        setSavingRedraw(false)

        if (result?.error) {
          setDrawHint({
            title: 'That outline could not be saved',
            body: 'Could not save the new outline. Try drawing it again.',
          })
          setDrawActive(true)
          return
        }

        dispatch(finishTerritoryRedraw())
        dispatch(setMapViewDialogOpen({ open: false }))
      })
    },
    [dispatch],
  )

  // A line only while splitting by one, since a territory is otherwise always a closed area.
  // `locked` keeps a drawn shape on screen while the dialog names it, while stopping a stray drag
  // behind the modal from starting a second one.
  useTerritoryDraw({
    map: canDrawTerritories && USE_MAPLIBRE ? mlMap : null,
    active: drawActive,
    mode: splittingFrom && splitMode === 'line' ? 'line' : 'area',
    locked: !!draftBoundary || !!splitLine,
    onComplete: (boundary) => {
      setDrawHint(null)

      // Reshaping a territory the rule already uses: there is nothing left to ask. The name,
      // team, owners and colour are already on the territory, so the new outline is saved on
      // the spot and the admin goes straight back to the rule.
      if (drawingForRule && redrawTerritoryId !== null) {
        const existing = territorySummaries.find(
          (t) => t.id === redrawTerritoryId,
        )

        setDrawActive(false)

        if (!existing) {
          // Deleted from under us, or never in the summaries. Returning empty-handed beats
          // saving a boundary against a guess at its name and team.
          dispatch(cancelTerritoryDraw())
          dispatch(setMapViewDialogOpen({ open: false }))
          return
        }

        // Straying onto another territory is the one thing worth stopping for here. Scanned
        // against the geometry already cached -- anything that could overlap a shape just drawn
        // on screen is on screen, so this needs no request and no await in the save path.
        const { overlaps } = findTerritoryOverlaps(
          boundary,
          territorySummaries.map((t) => ({
            id: t.id,
            name: t.name,
            bounds: t.bounds,
            boundary: territoryBoundaries[t.id] ?? null,
          })),
          { excludeId: existing.id },
        )

        if (overlaps.length) {
          setRedrawConfirm({ territory: existing, boundary })
          return
        }

        saveRedraw(existing, boundary)
        return
      }

      // Drawn for a rule: collect a name and go back. The full create dialog is skipped
      // entirely -- the rule supplies the team and owners.
      if (drawingForRule) {
        setNamingForRule(boundary)
        setDrawActive(false)
        return
      }

      setDraftBoundary(boundary)

      // A redraw carries the existing territory into the dialog, so the save becomes a PUT
      // that keeps its name, colour, team and owners and replaces only the shape.
      if (redrawingFor) {
        setEditing(redrawingFor)
        setRedrawingFor(null)
        setDrawActive(false)
        return
      }

      // A split keeps splittingFrom set: the dialog collects the new territory's details and
      // the save carves this shape out of that territory rather than creating a free-standing
      // one. Draw mode ends either way -- the stroke is the whole input.
      if (splittingFrom) {
        setDrawActive(false)
      }
    },
    onCompleteLine: (line) => {
      setDrawHint(null)
      setSplitLine(line)
      // Ends draw mode for the same reason the area split does: the stroke is the whole input,
      // and the dialog takes it from here.
      setDrawActive(false)
    },
    onDrawStart: () => setDrawHint(null),
    onInterrupted: () =>
      setDrawHint({
        title: 'That drawing was interrupted',
        body: 'That drawing was interrupted before it finished. Try drawing the loop again.',
      }),
    onReject: (reason: LassoRejection) => {
      // A tap or flick is almost always a slip, so it clears without comment. A loop that
      // crossed itself is a real attempt and needs saying, or the user repeats it -- which is
      // what happened before the dashed closing line and this message existed.
      //
      // A line cannot cross itself into an invalid shape, so the only way it fails here is by
      // being too short to be a stroke at all -- said plainly, because silence after a drag
      // that did nothing reads as the feature being broken.
      if (splittingFrom && splitMode === 'line') {
        setDrawHint({
          title: 'That line did not divide the territory',
          body: 'Draw a line that goes all the way across the territory, from one side to the other.',
        })
        return
      }

      setDrawHint(
        reason === 'crosses-itself'
          ? // Overshooting the start is repaired now, so a shape that still reaches here
            // crossed over itself somewhere in the middle -- which is ambiguous however it is
            // read, and the old advice ("finish near where you started") would be wrong.
            {
              title: 'That shape crossed over itself \u2014 just draw again',
              body: 'That shape crosses over itself in the middle. Draw one simple loop around the area.',
            }
          : null,
      )
    },
  })

  const {
    handleMapAfterAddLeadSaveSuccess: hereHandleAddLead,
    manualRefresh: hereManualRefresh,
  } = useMapEvents({
    hereMap: USE_MAPLIBRE ? undefined : hereMap || undefined,
    onViewChange: handleViewChange as never,
    centerProp: center,
    zoomProp: zoom,
    animatePositionChange,
    handleAddLeadSaveSuccess,
  })

  // refreshMapPins cast: GeoPoint is structurally compatible with H.geo.Point at runtime
  const {
    handleMapAfterAddLeadSaveSuccess: mlHandleAddLead,
    manualRefresh: mlManualRefresh,
  } = useMapLibreEvents({
    map: USE_MAPLIBRE ? mlMap : null,
    onViewChange: handleViewChange as any,
    handleAddLeadSaveSuccess,
  })

  const handleMapAfterAddLeadSaveSuccess = USE_MAPLIBRE
    ? mlHandleAddLead
    : hereHandleAddLead
  const manualRefresh = USE_MAPLIBRE ? mlManualRefresh : hereManualRefresh

  const handleReviewSaveSuccess = useCallback(
    (newPin: EntityLocationDTO) => {
      setReviewDraftId(null)
      handleMapAfterAddLeadSaveSuccess(newPin)
    },
    [handleMapAfterAddLeadSaveSuccess],
  )

  const handleReviewDuplicateManageClick = useCallback(() => {
    setReviewDraftId(null)
    handleDuplicateManageClick()
  }, [handleDuplicateManageClick])

  const handleRefreshButtonClick = useCallback(() => {
    manualRefresh()
    triggerRefresh()
  }, [manualRefresh, triggerRefresh])

  // After a KS save inside the multi-tenant drawer the tile re-fetch replaces
  // the cluster with the entity's new KS pin, but clickedPin still holds the
  // old cluster ID so the active-pin layer keeps rendering the cluster overlay.
  // Clearing clickedPin here drops that overlay immediately.
  const handleMultiTenantRefresh = useCallback(() => {
    setClickedPin(null)
    setHoveredPin(null)
    handleRefreshButtonClick()
  }, [handleRefreshButtonClick, setClickedPin, setHoveredPin])

  const handleLeadAutoCloseRefresh = useCallback(() => {
    setClickedPin(null)
    setHoveredPin(null)
    manualRefresh()
  }, [manualRefresh, setClickedPin, setHoveredPin])

  const handleSearchPan = useCallback(
    (hereResult: HereResult): Promise<void> => {
      return new Promise<void>((resolve) => {
        if (!mlMap) {
          resolve()
          return
        }

        const targetLng = hereResult.position.lng
        const targetLat = hereResult.position.lat
        const isHouseOrPlace =
          hereResult.resultType === 'houseNumber' ||
          hereResult.resultType === 'place'

        // If the map is already at the target, skip flyTo — moveend may not fire
        // for a zero-distance animation, which would leave the Promise unresolved.
        if (isHouseOrPlace || !hereResult.mapView) {
          const targetZoom = isHouseOrPlace ? 17 : 14
          const c = mlMap.getCenter()
          const alreadyThere =
            Math.abs(c.lng - targetLng) < 0.0001 &&
            Math.abs(c.lat - targetLat) < 0.0001 &&
            Math.abs(mlMap.getZoom() - targetZoom) < 0.1
          if (alreadyThere) {
            resolve()
            return
          }
          mlMap.once('moveend', () => resolve())
          mlMap.flyTo({ center: [targetLng, targetLat], zoom: targetZoom })
        } else {
          mlMap.once('moveend', () => resolve())
          mlMap.fitBounds([
            [hereResult.mapView.west, hereResult.mapView.south],
            [hereResult.mapView.east, hereResult.mapView.north],
          ])
        }
      })
    },
    [mlMap],
  )

  useRightDragZoom({
    map: USE_MAPLIBRE ? null : map,
    container: mapRef.current,
    minBoxPixels: 12,
  })

  const handleDrawerCloseOnTabFocus = useCallback(() => {
    setDrawerOpen(false)
    closeAndRefresh()
  }, [closeAndRefresh, setDrawerOpen])

  // Shared by both EntityDetailsDrawer and MultiTenantDrawer so the "View
  // Knock Status History" link always opens the modal on top of the open
  // drawer instead of closing it — see SH-1483.
  const handleViewKnockStatusHistory = useCallback(
    (location: EntityLocationDTO) => {
      setShowHistory(true)
      setAddressDetailsForKnockStatus(location)
      setOpenKnockStatusModal(true)
    },
    [setShowHistory, setAddressDetailsForKnockStatus, setOpenKnockStatusModal],
  )

  useRefreshOnTabFocus({
    drawerOpen,
    activePin: clickedPin || hoveredPin,
    manualRefresh,
    onDrawerClose: handleDrawerCloseOnTabFocus,
    setClickedPin,
  })

  return (
    <>
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

        {mounted && (
          <>
            <FilterChips
              hereMap={USE_MAPLIBRE ? undefined : map || undefined}
              searchDrawerOpen={searchDrawerOpen}
              filterDrawerOpen={filterDrawerOpen}
              onOpenFilterDrawer={() => setFilterDrawerOpen(true)}
              onMeasure={setChipsBottom}
            />

            <Box
              sx={{
                position: 'absolute',
                bottom: 28,
                right: 24,
                zIndex: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.6,
                justifyContent: 'flex-end',
              }}
            >
              {canDrawTerritories && (
                <TerritoryManageButton
                  count={territorySummaries.length}
                  onOpen={() => setManageOpen(true)}
                />
              )}
              <KeyInformation />
              {USE_MAPLIBRE && <CollisionToggle />}
              {salesPersonFilterEnabled && (
                <SalespersonFilterButton
                  onOpen={() => setSalespersonFilterModalOpen(true)}
                />
              )}
              <LocateMeButton
                hereMap={USE_MAPLIBRE ? undefined : map || undefined}
                mlMap={USE_MAPLIBRE ? mlMap : undefined}
                lastKnownPositionRef={
                  USE_MAPLIBRE ? lastKnownPositionRef : undefined
                }
              />
              <ZoomControls
                hereMap={USE_MAPLIBRE ? undefined : map || undefined}
                mlMap={USE_MAPLIBRE ? mlMap : undefined}
              />
              <LayerControls
                hereMap={USE_MAPLIBRE ? undefined : map || undefined}
                defaultLayers={defaultLayers as HereMapDefaultLayers}
                mlMap={USE_MAPLIBRE ? mlMap : undefined}
              />
              <RefreshButton onRefresh={handleRefreshButtonClick} />
            </Box>

            {canDrawTerritories &&
              (drawActive || drawingForRule) &&
              !namingForRule &&
              !draftBoundary &&
              !splitLine && (
                <TerritoryDrawBanner
                  hint={drawHint?.body ?? null}
                  hintTitle={drawHint?.title ?? null}
                  instruction={
                    drawingForRule && !splittingFrom && !drawActive
                      ? {
                          title:
                            redrawTerritoryId !== null
                              ? `Find “${
                                  territorySummaries.find(
                                    (t) => t.id === redrawTerritoryId,
                                  )?.name ?? 'the territory'
                                }”`
                              : parkedRuleName
                                ? `Where does the territory for “${parkedRuleName}” go?`
                                : 'Where does this territory go?',
                          detail:
                            'Move and zoom the map to the area, then start drawing. Drawing locks the map in place, so find the spot first.',
                        }
                      : drawingForRule && !splittingFrom
                        ? redrawTerritoryId !== null
                          ? {
                              title: `Redraw “${
                                territorySummaries.find(
                                  (t) => t.id === redrawTerritoryId,
                                )?.name ?? 'this territory'
                              }”`,
                              detail: savingRedraw
                                ? 'Saving the new outline…'
                                : 'Hold down and drag a loop around the new area. It replaces the current outline and keeps its name, team and owners.',
                            }
                          : {
                              title: parkedRuleName
                                ? `Draw the territory for “${parkedRuleName}”`
                                : 'Draw the territory for this rule',
                              detail:
                                'Hold down and drag a loop around the area. You will name it next, then go back to the rule.',
                            }
                        : splittingFrom
                          ? splitMode === 'line'
                            ? {
                                title: `Draw a line across ${splittingFrom.name}`,
                                detail:
                                  'Hold down and drag from one edge to the other. Let go to cut it in two.',
                              }
                            : {
                                title: 'Draw the part to split off',
                                detail:
                                  'Hold down and drag a loop around it. What is left stays with this territory.',
                              }
                          : null
                  }
                  altAction={
                    splittingFrom
                      ? {
                          label:
                            splitMode === 'line'
                              ? 'Draw a piece instead'
                              : 'Draw a line instead',
                          onClick: () => {
                            setSplitMode(splitMode === 'line' ? 'area' : 'line')
                            setDrawHint(null)
                          },
                        }
                      : drawingForRule && drawActive
                        ? // The map and the pen cannot both have the drag, so handing it back
                          // has to stay available: an admin who armed the pen over the wrong
                          // place needs the map again without leaving the trip.
                          {
                            label: 'Move the map instead',
                            onClick: () => {
                              setDrawActive(false)
                              setDrawHint(null)
                            },
                          }
                        : null
                  }
                  primaryAction={
                    drawingForRule && !drawActive
                      ? {
                          label: 'Start drawing',
                          onClick: () => {
                            setDrawActive(true)
                            setDrawHint(null)
                          },
                        }
                      : null
                  }
                  onCancel={() => {
                    setDrawActive(false)
                    setDraftBoundary(null)
                    setSplittingFrom(null)
                    setDrawHint(null)

                    // Came from a rule: leaving the pen idle on the map would strand them here
                    // with no way back, since the rule form is parked behind this dialog.
                    if (drawingForRule) {
                      dispatch(cancelTerritoryDraw())
                      dispatch(setMapViewDialogOpen({ open: false }))
                    }
                  }}
                />
              )}

            {canDrawTerritories && manageOpen && (
              <TerritoryManagePanel
                onClose={() => setManageOpen(false)}
                onZoomTo={zoomToTerritory}
                onEditDetails={async (territory) => {
                  zoomToTerritory(territory)

                  // PUT is a full replace and requires the boundary, which the summary feed
                  // omits. Request it if the cache has not already filled from the viewport.
                  //
                  // Awaited, and the panel is not closed until it lands. The dialog only renders
                  // once a boundary exists, so firing this off and closing the panel immediately
                  // meant a failed request left nothing on screen at all -- panel gone, no dialog,
                  // `editing` still set -- and the Edit click looked like it simply did nothing.
                  if (!territoryBoundaries[territory.id]) {
                    const result: any = await dispatch(
                      fetchTerritoryBoundaries([territory.id]) as never,
                    )

                    const boundary = (
                      result?.payload as TerritoryType[] | undefined
                    )?.find((t) => t.id === territory.id)?.boundary

                    if (result?.error || !boundary) {
                      // Left where they were, with something to act on, rather than staring at
                      // a map that swallowed the click.
                      toast.error(
                        "Could not load that territory's boundary. Please try again.",
                      )
                      return
                    }
                  }

                  setManageOpen(false)
                  setEditing(territory)
                }}
                onRedrawBoundary={(territory) => {
                  // Framed before the lasso arms, so the user is drawing over the territory
                  // they chose rather than wherever the map happened to be.
                  zoomToTerritory(territory)
                  setManageOpen(false)
                  setRedrawingFor(territory)
                  setDraftBoundary(null)
                  setDrawHint(null)
                  setDrawActive(true)
                }}
                onDelete={(territory) => {
                  // The list closes so the confirm sits over the actual shape.
                  zoomToTerritory(territory)
                  setManageOpen(false)
                  setPendingDelete(territory)
                }}
                onSplit={(territory) => {
                  // Framed and armed, exactly like a redraw -- the difference is only what
                  // happens to the shape once it is drawn.
                  zoomToTerritory(territory)
                  setManageOpen(false)
                  setSplittingFrom(territory)
                  setSplitMode('line')
                  setRedrawingFor(null)
                  setDraftBoundary(null)
                  setSplitLine(null)
                  // The banner carries the instructions now: it names the territory being cut
                  // and changes with the gesture, where a hint can only ever say one of them.
                  setDrawHint(null)
                  setDrawActive(true)
                }}
                onMerge={(territories) => {
                  setManageOpen(false)
                  setMerging(territories)
                }}
                onViewPerformance={() => {
                  // The panel is a modal; the report is a slideout over the live map, so the
                  // modal has to go or the map it flies around would be behind a backdrop.
                  setManageOpen(false)
                  closeAllDrawers()
                  setPerformanceOpen(true)
                }}
              />
            )}

            {canDrawTerritories && merging && merging.length > 1 && (
              <TerritoryMergeDialog
                selected={merging}
                onCancel={() => {
                  setMerging(null)
                  setManageOpen(true)
                }}
                onMerged={() => {
                  setMerging(null)
                  setManageOpen(true)
                }}
              />
            )}

            {canDrawTerritories && pendingDelete && (
              <TerritoryDeleteDialog
                territory={pendingDelete}
                // Both paths return to the list, so removing several in a row does not mean
                // reopening it each time.
                onCancel={() => {
                  setPendingDelete(null)
                  setManageOpen(true)
                }}
                onDeleted={() => {
                  setPendingDelete(null)
                  setManageOpen(true)
                }}
              />
            )}

            {canDrawTerritories &&
              editing &&
              (draftBoundary ?? territoryBoundaries[editing.id]) && (
                <TerritoryCreateDialog
                  territory={editing}
                  // Only on a REDRAW -- draftBoundary set means the shape changed, so the
                  // stored one is what it is being compared against. Editing details alone
                  // leaves the shape untouched, and a before/after of the same polygon would
                  // be noise.
                  previousBoundary={
                    draftBoundary ? territoryBoundaries[editing.id] : null
                  }
                  // After a redraw the new shape wins; otherwise the stored one is resent
                  // unchanged, since the endpoint will not accept an update without it.
                  boundary={(draftBoundary ?? territoryBoundaries[editing.id])!}
                  onClose={() => {
                    setEditing(null)
                    setDraftBoundary(null)
                  }}
                  onSaved={() => {
                    setEditing(null)
                    setDraftBoundary(null)
                  }}
                />
              )}

            {canDrawTerritories && !editing && (draftBoundary || splitLine) && (
              <TerritoryCreateDialog
                boundary={draftBoundary}
                splitFrom={splittingFrom}
                splitLine={splitLine}
                // Discards the shape but stays in draw mode, so "that came out wrong" costs one
                // click and another drag rather than re-entering the mode. splittingFrom
                // survives on purpose: a discarded cut should leave the user ready to cut the
                // same territory again, not quietly drawing a brand-new one. Exit drawing is
                // what leaves split mode.
                onClose={() => {
                  setDraftBoundary(null)
                  setSplitLine(null)
                  setDrawHint(null)
                }}
                onSaved={() => {
                  setDraftBoundary(null)
                  setSplitLine(null)
                  setDrawActive(false)
                  // Cleared on BOTH exits: a leftover splittingFrom would make the next plain
                  // drawing silently carve a piece out of an unrelated territory.
                  setSplittingFrom(null)
                  setDrawHint(null)
                }}
              />
            )}

            {canDrawTerritories && redrawConfirm && (
              <TerritoryOverlapConfirmDialog
                boundary={redrawConfirm.boundary}
                territoryId={redrawConfirm.territory.id}
                territoryName={redrawConfirm.territory.name}
                onCancel={() => {
                  // Back to the pen with the rule still parked: they came here to reshape this
                  // territory, and rejecting one outline is not abandoning that.
                  setRedrawConfirm(null)
                  setDrawActive(true)
                }}
                onConfirm={() => {
                  const { territory, boundary } = redrawConfirm
                  setRedrawConfirm(null)
                  saveRedraw(territory, boundary)
                }}
              />
            )}

            {canDrawTerritories && namingForRule && (
              <TerritoryNameDialog
                boundary={namingForRule}
                ruleName={parkedRuleName}
                onCancel={() => {
                  // Discards the shape and the trip, and hands them back to the rule form they
                  // were filling in -- which is still parked, because giving up on a drawing is
                  // not giving up on the rule.
                  setNamingForRule(null)
                  dispatch(cancelTerritoryDraw())
                  dispatch(setMapViewDialogOpen({ open: false }))
                }}
                onSave={(name) => {
                  setNamingForRule(null)
                  dispatch(
                    finishTerritoryDraw({ name, boundary: namingForRule }),
                  )
                  // Closing the map is the "back to the rule" half of the gesture: the rules
                  // page is still mounted behind this dialog and reopens its form on seeing the
                  // drafted shape.
                  dispatch(setMapViewDialogOpen({ open: false }))
                }}
              />
            )}

            {canDrawTerritories && (
              <TerritoryPerformanceDrawer
                show={performanceOpen}
                onClose={() => setPerformanceOpen(false)}
                onZoomTo={(territoryId) => {
                  const territory = territorySummaries.find(
                    (t) => t.id === territoryId,
                  )
                  if (territory) zoomToTerritory(territory)
                }}
              />
            )}

            {/* Hidden while a record drawer is open, for the same reason the pin bubbles are:
                the drawer is the detail view, and a bubble over it is in the way. */}
            {territoriesEnabled &&
              USE_MAPLIBRE &&
              mlMap &&
              selectedTerritory &&
              selectedTerritoryRecord &&
              !drawerOpen &&
              !multiTenantDrawerOpen && (
                <TerritorySummaryBubble
                  map={mlMap}
                  territory={selectedTerritoryRecord}
                  lngLat={selectedTerritory.lngLat}
                  alsoIn={selectedTerritory.otherTerritoryIds
                    .map((id) => territorySummaries.find((t) => t.id === id))
                    .filter((t): t is TerritoryType => !!t)}
                  onClose={() => setSelectedTerritory(null)}
                  // Admin-only, matching where the report is reachable from today. A rep gets
                  // the bubble without a dead end they cannot open.
                  onViewReport={
                    canDrawTerritories
                      ? () => {
                          setSelectedTerritory(null)
                          closeAllDrawers()
                          setPerformanceOpen(true)
                        }
                      : undefined
                  }
                />
              )}

            <RemoveMapBoundaryButton />

            {!USE_MAPLIBRE &&
              map &&
              contactLocations.map((loc) => {
                // Keep click-opened bubbles pinned until explicitly closed.
                const shouldShow =
                  ((clickedPin && isSameLocationIdentity(loc, clickedPin)) ||
                    (!clickedPin && loc.pinId === hoveredPin?.pinId)) &&
                  !drawerOpen &&
                  !multiTenantDrawerOpen &&
                  !mapLinkClickedContactLocation &&
                  loc.entityType?.toLowerCase() !== 'knockstatus'

                return shouldShow ? (
                  <MapPinTooltip
                    key={loc.pinId}
                    contactLocation={loc}
                    map={map}
                    onClose={onClose}
                    onActionClick={onActionClick}
                    onKnockStatusCleared={handleRefreshButtonClick}
                    onMouseEnter={() => setTooltipHovered(true)}
                    onMouseLeave={() => {
                      setTooltipHovered(false)
                      // Clear hovered pin if it's not the clicked one
                      if (
                        !openKnockStatusModal &&
                        !isSameLocationIdentity(loc, clickedPin!)
                      ) {
                        setHoveredPin(null)
                      }
                    }}
                  />
                ) : null
              })}

            {USE_MAPLIBRE &&
              mlMap &&
              contactLocations.map((loc) => {
                const shouldShow =
                  ((clickedPin && isSameLocationIdentity(loc, clickedPin)) ||
                    (!clickedPin && loc.pinId === hoveredPin?.pinId)) &&
                  !drawerOpen &&
                  !multiTenantDrawerOpen &&
                  !mapLinkClickedContactLocation &&
                  loc.entityType?.toLowerCase() !== 'knockstatus' &&
                  (!salesPersonFilterEnabled ||
                    isPinVisibleForSalespersonFilter(
                      loc,
                      salespersonFilterSelection,
                      salespersonFilterOptions,
                    ))

                return shouldShow ? (
                  <MapLibreMapPinTooltip
                    key={loc.pinId}
                    contactLocation={loc}
                    map={mlMap}
                    onClose={onClose}
                    onActionClick={onActionClick}
                    onKnockStatusCleared={handleRefreshButtonClick}
                    onMouseEnter={() => setTooltipHovered(true)}
                    onMouseLeave={() => {
                      setTooltipHovered(false)
                      if (!openKnockStatusModal) setHoveredPin(null)
                    }}
                  />
                ) : null
              })}

            {/* Keep the preview bubble visible when the clicked pin has been
                filtered out of contactLocations after a knock status save.
                filterLocationsNoKs() (Scenario 1 in useTileCache) removes entity
                pins that carry a KS association when no KS filter is active, so
                clickedPin may no longer appear in contactLocations even though the
                user has not explicitly dismissed the bubble. */}
            {USE_MAPLIBRE &&
              mlMap &&
              clickedPin &&
              !drawerOpen &&
              !multiTenantDrawerOpen &&
              !mapLinkClickedContactLocation &&
              clickedPin.entityType?.toLowerCase() !== 'knockstatus' &&
              !contactLocations.some((loc) =>
                isSameLocationIdentity(loc, clickedPin),
              ) &&
              (!salesPersonFilterEnabled ||
                isPinVisibleForSalespersonFilter(
                  clickedPin,
                  salespersonFilterSelection,
                  salespersonFilterOptions,
                )) && (
                <MapLibreMapPinTooltip
                  key={clickedPin.pinId}
                  contactLocation={clickedPin}
                  map={mlMap}
                  onClose={onClose}
                  onActionClick={onActionClick}
                  onKnockStatusCleared={handleRefreshButtonClick}
                  onMouseEnter={() => setTooltipHovered(true)}
                  onMouseLeave={() => {
                    setTooltipHovered(false)
                    if (!openKnockStatusModal) setHoveredPin(null)
                  }}
                />
              )}

            {!USE_MAPLIBRE &&
              map &&
              (!clickedPin || clickedPin.entityId === 0) &&
              addressDetailsForAddLeadTemp && (
                <AddLeadMapPinTooltip
                  map={map}
                  data={addressDetailsForAddLeadTemp}
                  checkHiddenRecords={!isHoverPreview() && !clickedPin}
                  onAddLead={handleLocationPreviewAddLead}
                  onMouseEnter={() => setTooltipHovered(true)}
                  onMouseLeave={() => {
                    if (
                      !clickedPin &&
                      !openKnockStatusModal &&
                      isHoverPreview() &&
                      addressDetailsForAddLeadTemp?.knockStatusLocationId
                    ) {
                      setAddressDetailsForAddLeadTemp(null)
                    }
                    setTooltipHovered(false)
                  }}
                  onClose={handleAddressDetailsForAddLeadClose}
                  onKnockStatusClick={handleLocationPreviewKnockStatus}
                  onViewKnockStatusHistory={
                    handleLocationPreviewKnockStatusHistory
                  }
                  onKnockStatusCleared={handleRefreshButtonClick}
                  pendingCustomReason={pendingCustomReason}
                />
              )}

            {USE_MAPLIBRE &&
              mlMap &&
              (!clickedPin || clickedPin.entityId === 0) &&
              addressDetailsForAddLeadTemp && (
                <MapLibreAddLeadMapPinTooltip
                  map={mlMap}
                  data={addressDetailsForAddLeadTemp}
                  checkHiddenRecords={!isHoverPreview() && !clickedPin}
                  onAddLead={handleLocationPreviewAddLead}
                  onMouseEnter={() => setTooltipHovered(true)}
                  onMouseLeave={() => {
                    if (
                      !clickedPin &&
                      !openKnockStatusModal &&
                      isHoverPreview() &&
                      addressDetailsForAddLeadTemp?.knockStatusLocationId
                    ) {
                      setAddressDetailsForAddLeadTemp(null)
                    }
                    setTooltipHovered(false)
                  }}
                  onClose={handleAddressDetailsForAddLeadClose}
                  onKnockStatusClick={handleLocationPreviewKnockStatus}
                  onViewKnockStatusHistory={
                    handleLocationPreviewKnockStatusHistory
                  }
                  onKnockStatusCleared={handleRefreshButtonClick}
                  pendingCustomReason={pendingCustomReason}
                  setPendingCustomReason={setPendingCustomReason}
                />
              )}
          </>
        )}

        {loading && (
          <MapLoadingProgress
            isRendering={Boolean(isMapRendering)}
            chipsBottom={chipsBottom}
          />
        )}
      </div>

      <MultiTenantDrawer
        onClose={closeAllDrawers}
        show={multiTenantDrawerOpen}
        drawerVariant="full"
        setDrawerOpen={setDrawerOpen}
        onBackClick={
          foundLocationFromSearch
            ? () => {
                setMultiTenantDrawerOpen(false)
                setDrawerOpen(false)
                setSearchDrawerOpen(true)
                closeAndRefresh()
              }
            : undefined
        }
        refreshTrigger={refreshTrigger}
        onRefresh={handleMultiTenantRefresh}
        setShowHistory={setShowHistory}
        onViewKnockStatusHistory={handleViewKnockStatusHistory}
        onReviewPendingLead={handleReviewPendingLead}
        onViewPestPacDetails={(location) =>
          viewClickHandler(location, true, { keepMultiTenantDrawerOpen: true })
        }
      />

      {drawerOpen && (
        <EntityDetailsDrawer
          onClose={() => {
            setDrawerOpen(false)
            if (!multiTenantDrawerOpen) {
              closeAndRefresh()
            }
          }}
          show={drawerOpen}
          multiTenantDrawerOpen={multiTenantDrawerOpen}
          drawerVariant={
            multiTenantDrawerOpen
              ? isUnfitBothModals
                ? 'full'
                : 'modal'
              : 'full'
          }
          onBackClick={
            foundLocationFromSearch
              ? () => {
                  setDrawerOpen(false)
                  setSearchDrawerOpen(true)
                  closeAndRefresh()
                }
              : multiTenantDrawerOpen
                ? () => setDrawerOpen(false)
                : undefined
          }
          refreshTrigger={refreshTrigger}
          onKnockStatusClick={(location) => {
            setShowHistory(false)
            setAddressDetailsForKnockStatus(location)
            setOpenKnockStatusModal(true)
          }}
          onViewKnockStatusHistory={handleViewKnockStatusHistory}
          onRefresh={handleRefreshButtonClick}
          onRefreshPins={handleLeadAutoCloseRefresh}
          onReviewPendingLead={handleReviewPendingLead}
          onPestPacAddLead={(location) => {
            setDrawerOpen(false)
            onActionClick('pestPacAddLead', location)
          }}
        />
      )}

      {salesPersonFilterEnabled && (
        <SalespersonFilterModal
          open={salespersonFilterModalOpen}
          onClose={() => setSalespersonFilterModalOpen(false)}
        />
      )}

      {filterDrawerOpen && (
        <FilterDrawer
          onClose={() => {
            setFilterDrawerOpen(false)
          }}
          show={filterDrawerOpen}
          closeAllDrawers={closeAllDrawers}
        />
      )}

      <SearchDrawer
        show={searchDrawerOpen}
        onClose={() => {
          setSearchDrawerOpen(false)
          setFoundLocationFromSearch(false)
        }}
        drawerVariant="full"
        toolbarWidth={72}
        map={USE_MAPLIBRE ? null : map}
        onFoundLocation={() => {
          setFoundLocationFromSearch(true)
        }}
        onPanToLocation={USE_MAPLIBRE ? handleSearchPan : undefined}
      />

      <AddLeadModal
        open={addLeadModalOpen}
        address={addressDetailsForAddLead}
        onClose={() => {
          handleAddLeadModalClose()
          setReviewDraftId(null)
        }}
        handleMapAfterAddLeadSaveSuccess={handleReviewSaveSuccess}
        handleDuplicateManageClick={handleReviewDuplicateManageClick}
        draftId={reviewDraftId}
      />

      {openKnockStatusModal && (
        <>
          {!showHistory ? (
            <KnockStatusModal
              open={openKnockStatusModal}
              onClose={handleKnockStatusModalClose}
              onRefresh={handleRefreshButtonClick}
              address={addressDetailsForKnockStatus}
              onDeleteSuccess={onKnockStatusDeleteSuccess}
              onSaveNewKnockStatusLocation={onSaveNewKnockStatusLocation}
            />
          ) : (
            <KnockStatusHistoryModal
              open={openKnockStatusModal}
              onClose={() => handleKnockStatusModalClose()}
              onRefresh={handleRefreshButtonClick}
              address={addressDetailsForKnockStatus}
              onUpdateAddressDetails={handleUpdateAddressDetails}
              onDeleteSuccess={onKnockStatusDeleteSuccess}
            />
          )}
        </>
      )}
    </>
  )
}
